import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';

const PlaybookStatusContext = createContext({});

export function usePlaybookStatus() {
  const context = useContext(PlaybookStatusContext);

  if (!context) {
    console.error('❌ usePlaybookStatus must be used within PlaybookStatusProvider');
    return { statuses: {}, isLoading: false };
  }

  return context;
}

const API_BASE_URL =
  typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

/** Keep query string under reverse-proxy limits (~4–8k); UUID lists grow ~37 chars per id */
const PERSON_STATUS_CHUNK = 45;
const BATCH_PARALLEL = 4;

let _noUserLoggedOnce = false;

export function PlaybookStatusProvider({ children, personIds = [], currentTenant: propTenant }) {
  const user = useUser();
  const contextTenant = useCurrentTenant();
  const tenantId = propTenant || contextTenant || user?.tenant_id || 'instructure';
  const userId = user?.id;

  const [statuses, setStatuses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const rawIds = (personIds || []).filter(Boolean).map(String);
    const uniqueSorted = [...new Set(rawIds)];

    if (!uniqueSorted.length) {
      setStatuses({});
      setIsLoading(false);
      return undefined;
    }

    if (!userId) {
      if (!_noUserLoggedOnce) {
        _noUserLoggedOnce = true;
        console.warn('PlaybookStatusProvider: No user context, skipping fetch');
      }
      setStatuses({});
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchBatchStatus = async () => {
      setIsLoading(true);
      try {
        const chunks = [];
        for (let i = 0; i < uniqueSorted.length; i += PERSON_STATUS_CHUNK) {
          chunks.push(uniqueSorted.slice(i, i + PERSON_STATUS_CHUNK));
        }

        const merged = {};

        for (let waveStart = 0; waveStart < chunks.length && !cancelled; waveStart += BATCH_PARALLEL) {
          const wave = chunks.slice(waveStart, waveStart + BATCH_PARALLEL);
          const waveResults = await Promise.all(
            wave.map((chunk) => {
              const idsParam = chunk.map(encodeURIComponent).join(',');
              const url = `${API_BASE_URL}/api/personas/batch-playbook-status?person_ids=${idsParam}&tenant_id=${encodeURIComponent(
                tenantId
              )}&user_id=${encodeURIComponent(userId)}`;
              return fetch(url, { credentials: 'include' }).then(async (response) =>
                response.ok ? response.json() : {}
              );
            })
          );
          if (cancelled || !mountedRef.current) break;
          for (const payload of waveResults) {
            if (payload && typeof payload === 'object') {
              Object.assign(merged, payload);
            }
          }
        }

        if (cancelled || !mountedRef.current) return;
        setStatuses(merged);
      } catch (error) {
        if (!cancelled && mountedRef.current && !_noUserLoggedOnce) {
          _noUserLoggedOnce = true;
          console.warn('PlaybookStatusProvider: Fetch failed', error?.message || error);
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchBatchStatus();
    return () => {
      cancelled = true;
    };
  }, [personIds, tenantId, userId]);

  const value = useMemo(() => ({ statuses, isLoading }), [statuses, isLoading]);

  return <PlaybookStatusContext.Provider value={value}>{children}</PlaybookStatusContext.Provider>;
}
